// tokenManager.js

import { IS_LOCAL_ENV } from '../constants/environment'; // Import local environment flag

// Retrieve authority and client IDs from environment variables
const authorityId = import.meta.env.VITE_AE_COGNITO_AUTHORITY;
const clientId = import.meta.env.VITE_AE_COGNITO_CLIENT_ID;

// Ensure required environment variables are present
if (!authorityId || !clientId) {
  console.warn(
    'Error: Authority ID or Client ID not found in environment variables'
  );
}

// Construct the key for sessionStorage based on authority and client ID
const storageKey = `oidc.user:${authorityId}:${clientId}`;

/**
 * Retrieves token data from sessionStorage, unless running in a local environment.
 * @returns {object|null} Token data object if valid, otherwise null.
 */
const getTokenData = () => {
  if (IS_LOCAL_ENV) return null; // Skip token retrieval in local environment

  try {
    const userData = JSON.parse(sessionStorage.getItem(storageKey));

    if (!userData) return null;

    if (
      userData.expires_at &&
      new Date(userData.expires_at * 1000) <= new Date()
    ) {
      console.warn('Token has expired');
      return null;
    }

    return userData;
  } catch (error) {
    console.error('Error parsing token data from sessionStorage:', error);
    return null;
  }
};

/**
 * Decodes a JWT token and returns its payload
 * @param {string} token - The JWT token to decode
 * @returns {object|null} Decoded token payload or null if invalid
 */
const decodeJwtToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64));
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

/**
 * Token Manager: Provides methods for handling authentication tokens.
 */
export const tokenManager = {
  getAccessToken: () =>
    IS_LOCAL_ENV ? null : getTokenData()?.access_token || null,

  getIdToken: () => (IS_LOCAL_ENV ? null : getTokenData()?.id_token || null),

  getRefreshToken: () =>
    IS_LOCAL_ENV ? null : getTokenData()?.refresh_token || null,

  getTokenExpiration: () => {
    if (IS_LOCAL_ENV) return null;
    const data = getTokenData();
    return data?.expires_at ? new Date(data.expires_at * 1000) : null;
  },

  isTokenValid: () => {
    if (IS_LOCAL_ENV) return false;
    const data = getTokenData();
    return data?.expires_at
      ? new Date(data.expires_at * 1000) > new Date()
      : false;
  },

  getUserInitials: () => {
    if (IS_LOCAL_ENV) return 'U';

    const idToken = getTokenData()?.id_token;
    if (!idToken) return 'U';

    const decodedToken = decodeJwtToken(idToken);
    if (!decodedToken) return 'U';

    const givenName = decodedToken.given_name;
    const familyName = decodedToken.family_name;

    if (!givenName && !familyName) return 'U';

    const firstInitial = givenName ? givenName.charAt(0).toUpperCase() : '';
    const lastInitial = familyName ? familyName.charAt(0).toUpperCase() : '';

    return `${firstInitial}${lastInitial}` || 'U';
  },

  getFullName: () => {
    if (IS_LOCAL_ENV) return 'User';

    const idToken = getTokenData()?.id_token;
    if (!idToken) return 'User';

    const decodedToken = decodeJwtToken(idToken);
    if (!decodedToken) return 'User';

    const givenName = decodedToken.given_name;
    const familyName = decodedToken.family_name;

    if (!givenName && !familyName) return 'User';

    // Handle cases where one name might be missing
    if (!givenName) return familyName;
    if (!familyName) return givenName;

    return `${givenName} ${familyName}`;
  },

  getUserEmail: () => {
    if (IS_LOCAL_ENV) return '';

    const idToken = getTokenData()?.id_token;
    if (!idToken) return '';

    const decodedToken = decodeJwtToken(idToken);
    if (!decodedToken) return '';

    const userEmail = decodedToken.preferred_username;

    if (!userEmail) return '';

    return userEmail;
  },
};
